import { HoverableElement } from '@99designs/common/components/HoverableElement';
import { Icon } from '@99designs/common/components/Icon';
import { Link } from '@99designs/common/components/Link';
import { AnchorMenuItemAction, Menu, MenuItemActionList } from '@99designs/common/components/Menu';
import { bemComponent } from '@99designs/common/utils/app';
import trackEvent from '@99designs/common/utils/platform/trackEvent';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import popupLink from 'app/ui/popupLink';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FieldInput, FieldTitle } from './field';

const MenuItem = bemComponent('menu__item', 'MenuItem');

type SocialMedia = 'facebook' | 'twitter' | 'pinterest' | 'shortURL';

interface SocialMediaShareButtonsProps {
    shareURLs: Record<SocialMedia, string>;
    trackEventOptions: trackShareClickOptions;
}

function SocialMediaShareButtons({ shareURLs, trackEventOptions }: SocialMediaShareButtonsProps) {
    const [open, setOpen] = useState(false);
    const handleClick = () => {
        setOpen((prev) => !prev);
    };
    const handleClickAway = () => {
        setOpen(false);
    };
    return (
        <HoverableElement
            isActive={open}
            hoverPosition="down-center"
            hoverElement={
                <ClickAwayListener onClickAway={handleClickAway}>
                    {/* The following is a `ref`-bearing `<div />` for `<ClickAwayListener />` to work as intended */}
                    <div>
                        <Menu variations={['constrained']}>
                            <MenuItemActionList>
                                <AnchorMenuItemAction
                                    url={shareURLs.facebook}
                                    icon={<Icon type="facebook-1" />}
                                    onClick={(e) => {
                                        popupLink(e);
                                        trackShareClick('facebook', trackEventOptions);
                                    }}
                                    rel="nofollow noopener noreferrer"
                                    target="_blank"
                                    data-popup-link={true}
                                >
                                    <FormattedMessage
                                        id="Share on Facebook"
                                        defaultMessage="Share on Facebook"
                                    />
                                </AnchorMenuItemAction>
                                <AnchorMenuItemAction
                                    url={shareURLs.twitter}
                                    icon={<Icon type="twitter-2" />}
                                    onClick={(e) => {
                                        popupLink(e);
                                        trackShareClick('twitter', trackEventOptions);
                                    }}
                                    rel="nofollow noopener noreferrer"
                                    target="_blank"
                                    data-popup-link={true}
                                    data-popup-height={260}
                                >
                                    <FormattedMessage
                                        id="Share on Twitter"
                                        defaultMessage="Share on Twitter"
                                    />
                                </AnchorMenuItemAction>
                                <AnchorMenuItemAction
                                    url={shareURLs.pinterest}
                                    icon={<Icon type="pinterest-1" />}
                                    onClick={(e) => {
                                        popupLink(e);
                                        trackShareClick('pinterest', trackEventOptions);
                                    }}
                                    rel="nofollow noopener noreferrer"
                                    target="_blank"
                                >
                                    <FormattedMessage
                                        id="Share on Pinterest"
                                        defaultMessage="Share on Pinterest"
                                    />
                                </AnchorMenuItemAction>
                            </MenuItemActionList>
                            <MenuItem variations={['separated']}>
                                <div className="field field--icon-label field--inverse m-none-bottom">
                                    <FieldInput>
                                        <FieldTitle>
                                            <Icon
                                                type="link-3"
                                                aria-label={
                                                    <FormattedMessage
                                                        id="Contest URL"
                                                        defaultMessage="Contest URL"
                                                    />
                                                }
                                            />
                                        </FieldTitle>
                                        <input
                                            className="input-text input-text--inverse"
                                            type="text"
                                            value={shareURLs.shortURL}
                                            onClick={(e) => {
                                                copyToClipboard(e);
                                                trackShareClick('short_url', trackEventOptions);
                                            }}
                                            data-auto-select-input
                                            readOnly
                                        />
                                    </FieldInput>
                                </div>
                            </MenuItem>
                        </Menu>
                    </div>
                </ClickAwayListener>
            }
        >
            <Icon type="share-1" variations={['offset-right']} />
            <Link onClick={handleClick} variations={['share-link']}>
                <FormattedMessage id="Share" defaultMessage="Share" />
            </Link>
        </HoverableElement>
    );
}
export default SocialMediaShareButtons;

function copyToClipboard(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
    e.currentTarget.setSelectionRange(0, e.currentTarget.value.length);
    document.execCommand('copy');
}

interface trackShareClickOptions {
    share_subject_type: string;
    location: string;
}
// @see https://github.com/99designs/analytics/blob/57f95fbdf12c9d5e781b7043885ee4c515f4ea5f/tracking-plan/common.yaml#L2
function trackShareClick(shareVia: string, options: trackShareClickOptions) {
    return trackEvent('Clicked Social Share Button', {
        share_via: shareVia,
        share_subject_type: options.share_subject_type,
        location: options.location,
    });
}



// WEBPACK FOOTER //
// ./src/web/js/contest/components/social-media-share-buttons.tsx